import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableSortLabel,
  Paper,
  Typography,
  Box,
} from "@mui/material";

import Popover from "@mui/material/Popover";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import Videocam from "@mui/icons-material/VideoCameraBackSharp";
import CloseIcon from "@mui/icons-material/Close";

import {
  FilterBucket,
  StakeKey,
  StakeReasonTeamFilters,
  StoryFilterKey,
  Team01,
  teamRgba,
  STORY_FILTER_ICON,
  RallyBadge,
} from "./GameStoryDialog_UPDATED";
import React from "react";

import type { SxProps, Theme } from "@mui/material/styles";
import { transform } from "typescript";
import { Visibility } from "@mui/icons-material";

export type FocusedReasonStakeForTeam = {
  team: Team01;
  reason: StoryFilterKey;
  stake: StakeKey;
};

export function deriveFocusedReasonStakeForSingleTeam(
  filters: StakeReasonTeamFilters | null | undefined,
): FocusedReasonStakeForTeam | null {
  if (!filters) return null;

  let found: FocusedReasonStakeForTeam | null = null;

  for (const st of Object.keys(filters) as StakeKey[]) {
    const byReason = filters[st];
    if (!byReason) continue;

    for (const reason of Object.keys(byReason) as StoryFilterKey[]) {
      const byTeam = byReason[reason];
      if (!byTeam) continue;

      const t0 = !!(byTeam as any)[0];
      const t1 = !!(byTeam as any)[1];

      if (t0) {
        const cur = {
          team: 0 as Team01,
          reason: reason as StoryFilterKey,
          stake: st as StakeKey,
        };
        if (found) return null; // multiple selected => not focused
        found = cur;
      }
      if (t1) {
        const cur = {
          team: 1 as Team01,
          reason: reason as StoryFilterKey,
          stake: st as StakeKey,
        };
        if (found) return null; // multiple selected => not focused
        found = cur;
      }
    }
  }

  return found;
}

type ReasonTableSortKey = "total" | "opportunityLost" | "gamePointLost";
type SortDir = "asc" | "desc";

type ActiveCellCol = "reason" | "gamePointLost" | "opportunityLost" | "total";

// video popup

export type RallyVideoPreviewSeg = {
  rallyIndex: number;
  /** absolute URL or blob URL to the rally video (same src for all tiles is fine) */
  src: string;
  /** segment bounds in seconds */
  startSec: number;
  endSec: number;
  /** optional label you might want to show in the tile */
  label?: string;
};

export function useLoopingVideoSegment(args: {
  enabled: boolean;
  startSec: number;
  endSec: number;
}) {
  const { enabled, startSec, endSec } = args;
  const ref = React.useRef<HTMLVideoElement | null>(null);

  React.useEffect(() => {
    const v = ref.current;
    if (!v) return;

    let raf = 0;

    const kick = async () => {
      if (!enabled) return;
      try {
        // Seek first, then play (muted+playsInline should allow autoplay)
        v.currentTime = Math.max(0, startSec);
        await v.play();
      } catch {
        // ignore autoplay rejections; user click usually fixes it
      }
    };

    const loop = () => {
      if (!enabled) return;
      const cur = v.currentTime || 0;
      if (cur >= endSec) {
        v.currentTime = Math.max(0, startSec);
      }
      raf = requestAnimationFrame(loop);
    };

    const onLoaded = () => {
      if (!enabled) return;
      kick();
      raf = requestAnimationFrame(loop);
    };

    v.addEventListener("loadedmetadata", onLoaded);

    // If metadata is already loaded, run immediately
    if (v.readyState >= 1) onLoaded();

    return () => {
      v.removeEventListener("loadedmetadata", onLoaded);
      cancelAnimationFrame(raf);
      try {
        v.pause();
      } catch {}
    };
  }, [enabled, startSec, endSec]);

  return ref;
}

function BadgeWithVideo(props: {
  badgeKey: StoryFilterKey;
  onOpenPreview: (anchorEl: HTMLElement, badgeKey: StoryFilterKey) => void;
  badgeOnClick?: () => void; // optional explicit badge click
  children: React.ReactNode;
}) {
  const { badgeKey, onOpenPreview, badgeOnClick, children } = props;

  const swallow = (e: React.SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.25 }}>
      <Box
        onClick={badgeOnClick}
        sx={{ display: "inline-flex", alignItems: "center", cursor: "pointer" }}
      >
        {children}
      </Box>

      <Tooltip title="Preview last 3 shots for this badge" disableInteractive>
        <IconButton
          size="small"
          onPointerDown={swallow as any}
          onMouseDown={swallow as any}
          onClick={(e: any) => {
            swallow(e);
            onOpenPreview(e.currentTarget as HTMLElement, badgeKey);
          }}
          sx={{ p: 0.15, opacity: 0.65, "&:hover": { opacity: 1 } }}
        >
          <VideocamOutlinedIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
}
// video popup

const otherTeam = (t: Team01): Team01 => (t === 0 ? 1 : 0);

export function TeamReasonsTable(props: {
  team: Team01;
  storyFilterBuckets: Record<StoryFilterKey, FilterBucket>;

  // selection state from parent (so we can highlight selected rows/cells)
  activeStoryFilters: StakeReasonTeamFilters;
  // NEW: parent-derived focus (single source of truth for “what’s selected”)
  focusedFilter: FocusedReasonStakeForTeam | null;
  focusedPlayerId?: number | null;

  // setters from parent
  // setActiveStoryFilters: React.Dispatch<
  //   React.SetStateAction<StakeReasonTeamFilters>
  // >;

  // optional: hide rows with 0 total
  hideZeros?: boolean;
  selectedRallyBadges: RallyBadge[];
  hoveredBadge?: RallyBadge | null;
  getReasonPreviewSegments: (args: {
    team: Team01;
    reason: StoryFilterKey;
    stake?: StakeKey | "both";
  }) => RallyVideoPreviewSeg[];
  onFocusedFilterChange: (f: FocusedReasonStakeForTeam | null) => void;
}) {
  const {
    team,
    storyFilterBuckets,
    activeStoryFilters,
    //setActiveStoryFilters,
    hideZeros = true,
    selectedRallyBadges,
    hoveredBadge,
    getReasonPreviewSegments,
    onFocusedFilterChange,
    focusedFilter,
    focusedPlayerId,
  } = props;

  const teamDisplayName = team === 0 ? "Team 0" : "Team 1";
  const teamLabel =
    teamDisplayName ?? `Team ${team}`;

  const teamColor = teamRgba(team, 1);

  const hoverReason = hoveredBadge?.team === team ? hoveredBadge.key : null;
  const hoverStake = hoveredBadge?.team === team ? hoveredBadge.stake : null;

  const hoverSx = {
    //bgcolor: "blue ! important", //"rgba(255,255,255,0.12)",
    background: teamRgba(team, 0.12), //"blue ! important", //"rgba(255,255,255,0.12)",
    backgroundColor: teamRgba(team, 0.12),
    outline: "1px solid rgba(255,255,255,0.25)! important",
    outlineOffset: "-1px",
  } as const;

  // console.error("hovered badge", JSON.stringify(hoveredBadge));

  const [previewPos, setPreviewPos] = React.useState<{
    top: number;
    left: number;
  } | null>(null);
  const [previewStake, setPreviewStake] = React.useState<StakeKey | "both">(
    "both",
  );
  const dragRef = React.useRef<{
    startX: number;
    startY: number;
    startTop: number;
    startLeft: number;
  } | null>(null);

  const [previewSize, setPreviewSize] = React.useState<{
    w: number;
    h: number;
  }>({
    w: 720,
    h: 420,
  });

  const resizeRef = React.useRef<{
    corner: "tl" | "tr" | "bl" | "br";
    startX: number;
    startY: number;
    startW: number;
    startH: number;
    startTop: number;
    startLeft: number;
  } | null>(null);

  const [previewReason, setPreviewReason] =
    React.useState<StoryFilterKey | null>(null);

  const previewOpen = Boolean(previewPos) && previewReason != null;

  const previewSegs = React.useMemo(() => {
    if (!previewReason) return [];
    const segs =
      getReasonPreviewSegments?.({
        team,
        reason: previewReason,
        stake: previewStake,
      }) ?? [];
// console.error({ segs });
    return segs;
  }, [getReasonPreviewSegments, team, previewReason]);

  const [sortKey, setSortKey] = React.useState<ReasonTableSortKey>("total");
  const [sortDir, setSortDir] = React.useState<SortDir>("desc");

  // purely UI selection: last clicked cell highlight (independent of filters)
  const [activeCell, setActiveCell] = React.useState<{
    reason: StoryFilterKey;
    col: ActiveCellCol;
  } | null>(null);

  React.useEffect(() => {
    // If another team becomes focused, clear this table’s “last clicked cell” highlight.
    if (!focusedFilter) {
      setActiveCell(null);
      return;
    }
    if (focusedFilter.team !== team) {
      setActiveCell(null);
      return;
    }
  }, [focusedFilter, team]);

  function rallyHas(reason: StoryFilterKey, st: StakeKey): boolean {
    return selectedRallyBadges.some(
      (b) => b.team === team && b.key === reason && b.stake === st,
    );
  }

  function rallyHasAny(reason: StoryFilterKey): boolean {
    return (
      rallyHas(reason, "gamePointLost") || rallyHas(reason, "opportunityLost")
    );
  }

  function isActive(reason: StoryFilterKey, col: ActiveCellCol): boolean {
    return activeCell?.reason === reason && activeCell?.col === col;
  }
  function countFor(team: Team01, b: FilterBucket, st: StakeKey): number {
    const s = b.stakes[st];

    if (focusedPlayerId != null) {
      return s.players[focusedPlayerId]?.size ?? 0;
    }

    return s.teams[team]?.size ?? 0;
  }

  function isSelected(
    team: Team01,
    reason: StoryFilterKey,
    st: StakeKey,
  ): boolean {
    return !!activeStoryFilters?.[st]?.[reason]?.[team];
  }

  function isFocusedCell(
    stake: StakeKey,
    reason: StoryFilterKey,
    team: Team01,
  ) {
    return (
      props.focusedFilter?.stake === stake &&
      props.focusedFilter?.reason === reason &&
      props.focusedFilter?.team === team
    );
  }

  function setSelected(args: {
    stake: StakeKey;
    reason: StoryFilterKey;
    team: Team01;
  }) {
    const same =
      props.focusedFilter &&
      props.focusedFilter.stake === args.stake &&
      props.focusedFilter.reason === args.reason &&
      props.focusedFilter.team === args.team;

    props.onFocusedFilterChange(same ? null : args);
  }

  const rows = React.useMemo(() => {
    const arr = (Object.keys(storyFilterBuckets) as StoryFilterKey[]).map(
      (k) => {
        const b = storyFilterBuckets[k];
        const opp = countFor(team, b, "opportunityLost");
        const gp = countFor(team, b, "gamePointLost");
        const total = opp + gp;
        // console.error("totals for k", { k, opp, gp, total });
        return { k, b, opp, gp, total };
      },
    );
    //.filter((r) => (hideZeros ? r.total > 0 : true));

    const mult = sortDir === "asc" ? 1 : -1;

    // arr.sort((a, c) => {
    //   const av =
    //     sortKey === "total"
    //       ? a.total
    //       : sortKey === "opportunityLost"
    //         ? a.opp
    //         : a.gp;
    //   const cv =
    //     sortKey === "total"
    //       ? c.total
    //       : sortKey === "opportunityLost"
    //         ? c.opp
    //         : c.gp;
    //   return (av - cv) * mult;
    // });

    arr.sort((a, c) => {
      return a.b.label.localeCompare(c.b.label); //  a.k.localeCompare(c.k);
    });

    return arr;
  }, [storyFilterBuckets, team, sortKey, sortDir, hideZeros]);

  const onSort = (k: ReasonTableSortKey) => {
    if (sortKey === k) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(k);
      setSortDir("desc");
    }
  };

  const hoveredReason: StoryFilterKey | null =
    hoveredBadge && hoveredBadge.team === team ? hoveredBadge.key : null;

  const hoveredStake: StakeKey | null =
    hoveredBadge && hoveredBadge.team === team ? hoveredBadge.stake : null;

  const hoverBg = teamRgba(team, 0.1);
  const hoverOutline = `1px solid ${teamRgba(team, 0.22)}`;

  const cellSx = (args: {
    clickable: boolean;
    selected: boolean;
    isActiveCell: boolean;
    leftAlign?: boolean;
    rally?: boolean;
    isLabelRow?: boolean;
    isZero?: boolean;
    hovered?: boolean;
    //alignRight?: boolean;
  }): SxProps<Theme> => {
    const {
      clickable,
      selected,
      isActiveCell,
      leftAlign,
      rally,
      isLabelRow,
      isZero,
      hovered,
    } = args;

    // console.error({ clickable, selected, isActiveCell, leftAlign, rally, isLabelRow })
    // Compact, spreadsheet-ish feel.
    return {
      whiteSpace: "nowrap",
      cursor: clickable ? "pointer" : "default",
      userSelect: "none",
      fontSize: 11,
      lineHeight: 1,
      py: 0.22,
      px: 0.45,
      textAlign: leftAlign ? "left" : "center",
      fontWeight: (selected && !isZero) || rally ? 900 : 400,
      bgcolor:
        (selected && !isZero) || hovered
          ? "rgba(255,255,255,0.10)"
          : "transparent",
      background:
        selected && !isZero && (rally || isLabelRow)
          ? "rgba(0,0,0,0.05)"
          : "none",

      ...(isActiveCell
        ? {
            outline: "2px solid rgba(255,255,255,0.45)",
            outlineOffset: "-2px",
            bgcolor: "rgba(255,255,255,0.08)",
          }
        : {}),
      ...(clickable
        ? { "&:hover": { bgcolor: "rgba(255,255,255,0.08)" } }
        : {}),
    };
  }; //cellSx

  let focusedOnOtherTeamPlayer = false;

  if (focusedPlayerId != null) {
    if ((focusedPlayerId === 0 || focusedPlayerId === 1) && team !== 0) {
      focusedOnOtherTeamPlayer = true;
    }
    if ((focusedPlayerId === 2 || focusedPlayerId === 3) && team !== 1) {
      focusedOnOtherTeamPlayer = true;
    }
  }

  return (
    <Paper
      data-demo="team-reasons-table"
      data-demo-team={team}
      elevation={0}
      sx={{
        p: 0.5,
        borderRadius: 2,
        border: "1px solid rgba(255,255,255,0.16)",
        bgcolor: "rgba(0,0,0,0.0)",
        // ✅ shrink-wrap to content (don't fill the flex row)
        display: "block",
        visibility: focusedOnOtherTeamPlayer ? "hidden" : "visible",
        width: "100%", //"fit-content",
        maxWidth: "100%",
        flex: "1 1 auto",
      }}
    >
      {(() => {
        const teamColor = team === 0 ? teamRgba(0, 1) : teamRgba(1, 1);

        return (
          <Box
            data-team={team}
            sx={{ display: "flex", alignItems: "center", gap: 0.7, mb: 0.35 }}
          >
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: "3px",
                bgcolor: teamColor,
                color: "white",
                boxShadow: `0 0 0 1px rgba(0,0,0,0.35) inset`,
              }}
            />
            <Typography
              sx={{
                fontWeight: 900,
                fontSize: 12,
                lineHeight: 1.05,
                color: "white",
              }}
            >
              Reasons for Losing Rally: {teamLabel}
              {typeof focusedPlayerId === "number"
                ? " Player p" + focusedPlayerId
                : ""}
            </Typography>
          </Box>
        );
      })()}

      <Table
        size="small"
        sx={{
          width: "auto",
          tableLayout: "auto",
          borderCollapse: "separate",
          borderSpacing: 0,
          "& td, & th": {
            padding: "0 ! important",
            paddingLeft: "2px !important",
            paddingRight: "2px !important",
            whiteSpace: "nowrap",
            borderBottom: "1px solid rgba(255,255,255,0.08)",
            color: "white",
          },
        }}
      >
        <TableHead>
          <TableRow
            sx={{
              borderTop: "1px solid rgba(255,255,255,0.22)",
              borderBottom: "1px solid rgba(255,255,255,0.22)",
            }}
          >
            <TableCell
              sx={{
                fontWeight: 900,
                minWidth: 0,
                fontSize: 12,
                py: 0.4,
                px: 0.75,
                borderRight: "1px solid rgba(255,255,255,0.10)",
                verticalAlign: "bottom",
              }}
            >
              Reason
            </TableCell>

            <TableCell
              align="right"
              sx={{
                fontWeight: 900,
                // width: 110,
                minWidth: 0,
                fontSize: 12,
                py: 0.4,
                px: 0.75,
                borderRight: "1px solid rgba(255,255,255,0.10)",
                verticalAlign: "bottom",
                textAlign: "center",
              }}
            >
              <TableSortLabel
                sx={{ whiteSpace: "nowrap" }}
                active={sortKey === "gamePointLost"}
                direction={sortDir}
                onClick={() => onSort("gamePointLost")}
              >
                Game Pts Lost
              </TableSortLabel>
            </TableCell>

            <TableCell
              align="right"
              sx={{
                fontWeight: 900,
                minWidth: 0,

                fontSize: 12,
                py: 0.4,
                px: 0.75,
                borderRight: "1px solid rgba(255,255,255,0.10)",
                verticalAlign: "bottom",
                textAlign: "center",
              }}
            >
              <TableSortLabel
                sx={{ whiteSpace: "nowrap" }}
                active={sortKey === "opportunityLost"}
                direction={sortDir}
                onClick={() => onSort("opportunityLost")}
              >
                Opportunities Lost
              </TableSortLabel>
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {rows.map(({ k, b, opp, gp, total }) => {
            const selOpp = isSelected(team, k, "opportunityLost");
            const selGp = isSelected(team, k, "gamePointLost");
            const selAny = selOpp || selGp;

            const Icon = STORY_FILTER_ICON[k]; // or k, depending on your loop

            let translate = "translateY(0px)";
            if (k === "forcedFault") {
              translate = "translateY(3px)";
            }

            function swallow(e: React.SyntheticEvent) {
              e.preventDefault();
              e.stopPropagation();
            }

            function NumCell(args: {
              value: number;
              reason: StoryFilterKey;
              stake: StakeKey;
            }) {
              const { value, reason, stake } = args;

              if (reason === "missed3rdDrive" && stake === "gamePointLost") {
                return null;
              }
              if (reason === "missed3rdDrop" && stake === "gamePointLost") {
                return null;
              }
              if (reason === "missedServe" && stake === "gamePointLost") {
                return null;
              }
              if (reason === "missedReturn" && stake === "opportunityLost") {
                return null;
              }
              // if (value === 0) {
              //   return null;
              // }

              return (
                <Box
                  sx={{
                    alignItems: "center",
                    justifyContent: "flex-start",
                    gap: 0.5,
                    display: "inline-flex",
                  }}
                >
                  {/* BADGE AREA (clicking this should still filter) */}
                  <Box
                    data-team={team}
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      cursor: "pointer",
                    }}
                    // IMPORTANT: do NOT stop propagation here — let the TableCell onClick run
                  >
                    <Box
                      sx={{
                        border: `2px solid transparent`,
                        "&:hover": {
                          opacity: 1,
                          border: `2px solid ${teamRgba(team, 0.9)}`,
                        },
                      }}
                    >
                      <Box
                        sx={{
                          fontSize: 12,
                          color: teamRgba(team, 1),
                          opacity: 0.9,
                          flex: "0 0 auto",
                          marginRight: "4px",
                          transform: translate,
                          display: "inline-flex",
                        }}
                      >
                        {React.createElement(Icon as React.ElementType)}
                      </Box>
                    </Box>

                    {/* NUMBER AREA (also filters via TableCell onClick) */}
                    <Box sx={{ cursor: "pointer" }}>
                      <span>{value}</span>
                    </Box>
                  </Box>

                  {/* VIDEO ICON (opens preview only; MUST swallow all pointer/click events) */}
                  {/* <Tooltip
                    title={
                      reason +
                      ": preview last 3 shots for this badge (across all matching rallies)"
                    }
                    disableInteractive
                  >
                    <IconButton
                      data-team={team}
                      size="small"
                      onPointerDown={swallow as any}
                      onMouseDown={swallow as any}
                      onClick={(e: any) => {
                        swallow(e);
                        setPreviewReason(reason);
                        setPreviewStake(stake);
                        setPreviewPos({ top: e.clientY, left: e.clientX }); // ✅ open near click
                      }}
                      sx={{
                        ml: 0.5,
                        p: 0.25,
                        opacity: 0.65,
                        border: `2px solid transparent`,
                        "&:hover": {
                          opacity: 1,
                          border: `2px solid ${teamRgba(team, 0.9)}`,
                        },
                      }}
                    >
                      <Videocam sx={{ fontSize: 16, color: teamColor }} />
                    </IconButton>
                  </Tooltip> */}
                </Box>
              );
            }

            return (
              <TableRow key={k} hover>
                <TableCell
                  data-team={team}
                  sx={{
                    ...cellSx({
                      hovered: hoveredReason === k,
                      isLabelRow: true,
                      clickable: false,
                      selected: selAny,
                      leftAlign: true,
                      isActiveCell:
                        activeCell?.reason === k &&
                        activeCell?.col === "reason",
                    }),
                    // ...hoverSx,
                    ...(hoverReason === k ? hoverSx : {}), // {}),
                  }}
                  //   onClick={() => {
                  //     setActiveCell({ reason: k, col: "reason" });
                  //     setSelected({ reason: k, team, stake: "both" });
                  //   }}
                  //   title="Click to filter (both stakes)"
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
                    {(() => {
                      const Icon = STORY_FILTER_ICON[k];
                      return (
                        <Box
                          sx={{
                            fontSize: 16,
                            color: teamColor,
                            opacity: 0.95,
                            flex: "0 0 auto",
                            transform: translate,
                            display: "inline-flex",
                          }}
                        >
                          {React.createElement(Icon as React.ElementType)}
                        </Box>
                      );
                    })()}
                    <span>{b.label}</span>
                  </Box>
                </TableCell>

                <TableCell
                  align="center"
                  data-demo="reason-cell"
                  data-demo-team={team}
                  data-demo-reason={k}
                  data-demo-stake="gamePointLost"
                  data-demo-value={gp}
                  sx={{
                    ...cellSx({
                      hovered:
                        hoveredReason === k && hoveredStake === "gamePointLost",

                      clickable: true && gp > 0,
                      isZero: gp === 0,
                      selected: selGp,
                      isActiveCell:
                        activeCell?.reason === k &&
                        activeCell?.col === "gamePointLost",
                      rally: rallyHas(k, "gamePointLost"),
                    }),
                    ...(hoverReason === k && hoverStake === "gamePointLost"
                      ? hoverSx
                      : {}),
                    ...{ height: "31px" },
                  }}
                  onClick={() => {
                    if (gp > 0) {
                      setActiveCell({ reason: k, col: "gamePointLost" });
                      setSelected({ reason: k, team, stake: "gamePointLost" });
                    }
                  }}
                  title={undefined} //"Click to filter (game point lost)"
                >
                  <>
                    {/* {gp} */}
                    <NumCell value={gp} reason={k} stake={"gamePointLost"} />
                  </>
                </TableCell>

                <TableCell
                  align="right"
                  data-demo="reason-cell"
                  data-demo-team={team}
                  data-demo-reason={k}
                  data-demo-stake="opportunityLost"
                  data-demo-value={opp}
                  sx={{
                    ...cellSx({
                      hovered:
                        hoveredReason === k &&
                        hoveredStake === "opportunityLost",

                      clickable: true && opp > 0,
                      isZero: opp === 0,
                      selected: selOpp,
                      isActiveCell:
                        activeCell?.reason === k &&
                        activeCell?.col === "opportunityLost",
                      rally: rallyHas(k, "opportunityLost"),
                    }),
                    ...(hoverReason === k && hoverStake === "opportunityLost"
                      ? hoverSx
                      : {}),
                  }}
                  onClick={() => {
                    if (opp > 0) {
                      setActiveCell({ reason: k, col: "opportunityLost" });
                      setSelected({
                        reason: k,
                        team,
                        stake: "opportunityLost",
                      });
                    }
                  }}
                  //   title="Click to filter (opportunity lost)"
                  title={undefined} //"Click to filter (game point lost)"
                >
                  {/* {opp} */}
                  <NumCell value={opp} reason={k} stake={"opportunityLost"} />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <Popover
        open={previewOpen}
        onClose={() => {
          setPreviewPos(null);
          setPreviewReason(null);
        }}
        anchorReference="anchorPosition"
        anchorPosition={
          previewPos
            ? { top: previewPos.top, left: previewPos.left }
            : undefined
        }
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            p: 1,
            borderRadius: 2,
            bgcolor: "rgba(250,250,250)",
            border: "1px solid rgba(0,0,0,0.12)",
            boxShadow: "0 10px 40px rgba(0,0,0,0.45)",

            // ❌ remove native CSS resize (only bottom-right)
            // resize: "both",

            overflow: "hidden",
            position: "relative",

            // ✅ controlled size (resizable via our handles)
            width: previewSize.w,
            height: previewSize.h,
            minWidth: 360,
            minHeight: 260,
            maxWidth: "92vw",
            maxHeight: "86vh",

            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        {/* HEADER: draggable handle */}
        <Box
          onPointerDown={(e: any) => {
            // start drag
            e.preventDefault();
            e.stopPropagation();
            if (!previewPos) return;

            dragRef.current = {
              startX: e.clientX,
              startY: e.clientY,
              startTop: previewPos.top,
              startLeft: previewPos.left,
            };

            const onMove = (ev: PointerEvent) => {
              const st = dragRef.current;
              if (!st) return;
              const dx = ev.clientX - st.startX;
              const dy = ev.clientY - st.startY;
              setPreviewPos({ top: st.startTop + dy, left: st.startLeft + dx });
            };

            const onUp = () => {
              dragRef.current = null;
              window.removeEventListener("pointermove", onMove);
              window.removeEventListener("pointerup", onUp);
            };

            window.addEventListener("pointermove", onMove);
            window.addEventListener("pointerup", onUp);
          }}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 0.75,
            cursor: "grab",
            userSelect: "none",
            px: 0.25,
          }}
        >
          <Box
            sx={{
              display: "flex",
              position: "relative",
              flexDirection: "column",
              gap: 0.25,
            }}
          >
            <Box
              sx={{
                fontSize: 12,
                fontWeight: 900,
                color: "black",
                marginLeft: "28px",
              }}
            >
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: 0.75,
                  bgcolor: teamColor,
                  display: "inline-block",
                  marginRight: "6px",
                  transform: "translateY(2px)",
                  border: "1px solid rgba(0,0,0,0.25)",
                  flex: "0 0 auto",
                }}
              />
              {teamLabel}
              {/* {previewStake === "both" ? "both stakes" : previewStake} •{"  "} */}
              {/* {previewSegs.length} rallies */}
              <IconButton
                size="small"
                onClick={() => {
                  setPreviewPos(null);
                  setPreviewReason(null);
                }}
                sx={{
                  position: "absolute",
                  right: "-40px",
                  display: "inline-block",
                  p: 0.25,
                  opacity: 0.8,
                  "&:hover": { opacity: 1 },
                }}
              >
                <CloseIcon sx={{ fontSize: 16, color: "rgba(0,0,0)" }} />
              </IconButton>
            </Box>

            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 900,
                color: "black",
                marginLeft: "28px",
              }}
            >
              {/* ✅ label on reason info */}
              {previewStake}:{" "}
              {previewReason
                ? storyFilterBuckets[previewReason]?.label
                : "Preview"}{" "}
              ({previewSegs.length} rallies)
            </Typography>
          </Box>
        </Box>

        {(["tl", "tr", "bl", "br"] as const).map((corner) => (
          <Box
            key={corner}
            onPointerDown={(e: any) => {
              e.preventDefault();
              e.stopPropagation();
              if (!previewPos) return;

              resizeRef.current = {
                corner,
                startX: e.clientX,
                startY: e.clientY,
                startW: previewSize.w,
                startH: previewSize.h,
                startTop: previewPos.top,
                startLeft: previewPos.left,
              };

              const onMove = (ev: PointerEvent) => {
                const st = resizeRef.current;
                if (!st) return;

                const dx = ev.clientX - st.startX;
                const dy = ev.clientY - st.startY;

                const minW = 360;
                const minH = 260;

                // start from original
                let w = st.startW;
                let h = st.startH;
                let top = st.startTop;
                let left = st.startLeft;

                if (st.corner === "br") {
                  w = st.startW + dx;
                  h = st.startH + dy;
                } else if (st.corner === "bl") {
                  w = st.startW - dx;
                  h = st.startH + dy;
                  left = st.startLeft + dx;
                } else if (st.corner === "tr") {
                  w = st.startW + dx;
                  h = st.startH - dy;
                  top = st.startTop + dy;
                } else if (st.corner === "tl") {
                  w = st.startW - dx;
                  h = st.startH - dy;
                  left = st.startLeft + dx;
                  top = st.startTop + dy;
                }

                // clamp (and keep anchored nicely)
                const clampedW = Math.max(minW, w);
                const clampedH = Math.max(minH, h);

                // if we clamped while resizing from left/top, adjust position so the opposite corner stays put
                if (st.corner === "bl" || st.corner === "tl") {
                  const wDiff = clampedW - w;
                  left -= wDiff; // move back left if we clamped smaller than intended
                }
                if (st.corner === "tr" || st.corner === "tl") {
                  const hDiff = clampedH - h;
                  top -= hDiff;
                }

                setPreviewSize({ w: clampedW, h: clampedH });
                setPreviewPos({ top, left });
              };

              const onUp = () => {
                resizeRef.current = null;
                window.removeEventListener("pointermove", onMove);
                window.removeEventListener("pointerup", onUp);
              };

              window.addEventListener("pointermove", onMove);
              window.addEventListener("pointerup", onUp);
            }}
            sx={{
              position: "absolute",
              width: 24,
              height: 24,
              borderRadius: 1,
              bgcolor: "rgba(0,0,0,0.10)",
              border: "1px solid rgba(0,0,0,0.25)",
              zIndex: 5,

              // ✅ draw a corner "L" mark (readable as resize)
              "&::after": {
                content: '""',
                position: "absolute",
                left: 7,
                top: 7,
                width: 10,
                height: 10,
                // pick which sides of the "L" based on corner
                borderLeft:
                  corner === "tl" || corner === "bl"
                    ? "2px solid rgba(0,0,0,0.55)"
                    : "none",
                borderRight:
                  corner === "tr" || corner === "br"
                    ? "2px solid rgba(0,0,0,0.55)"
                    : "none",
                borderTop:
                  corner === "tl" || corner === "tr"
                    ? "2px solid rgba(0,0,0,0.55)"
                    : "none",
                borderBottom:
                  corner === "bl" || corner === "br"
                    ? "2px solid rgba(0,0,0,0.55)"
                    : "none",

                borderRadius: 1,
              },

              ...(corner === "tl"
                ? { top: 4, left: 4, cursor: "nwse-resize" }
                : {}),
              ...(corner === "tr"
                ? { top: 4, right: 4, cursor: "nesw-resize" }
                : {}),
              ...(corner === "bl"
                ? { bottom: 4, left: 4, cursor: "nesw-resize" }
                : {}),
              ...(corner === "br"
                ? { bottom: 4, right: 4, cursor: "nwse-resize" }
                : {}),
            }}
          />
        ))}

        {/* BODY: scrollable + grid auto-resizes */}
        {previewSegs.length === 0 ? (
          <Typography sx={{ fontSize: 12, opacity: 0.75, p: 1 }}>
            No preview clips available.
          </Typography>
        ) : (
          <Box
            sx={{
              minHeight: 0, // ✅ critical for proper flex scrolling
              alignContent: "start", // ✅ prevents that “blank row” feel
              flex: "1 1 auto",
              overflow: "auto",
              pr: 0.5,
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
              gap: 0.75,
            }}
          >
            {previewSegs
              .slice(0, 24)
              .sort((s1, s2) => s1.rallyIndex - s2.rallyIndex)
              .map((seg) => (
                <PreviewTile
                  key={`${seg.rallyIndex}-${seg.startSec}`}
                  seg={seg}
                  team={team}
                />
              ))}
          </Box>
        )}
      </Popover>
    </Paper>
  );
}

export function PreviewTile(props: {
  seg: RallyVideoPreviewSeg;
  team: Team01;
  height?: number;
}) {
  const { seg, team, height } = props;

  const videoRef = useLoopingVideoSegment({
    enabled: true,
    startSec: seg.startSec,
    endSec: seg.endSec,
  });

  return (
    <Box
      sx={{
        position: "relative",
        borderRadius: 1.5,
        overflow: "hidden",
        border: `1px solid ${teamRgba(team, 1)}`,
        bgcolor: "rgba(0,0,0,0.35)",
        height: height || 140,
      }}
    >
      <video
        ref={videoRef}
        src={seg.src}
        muted
        playsInline
        autoPlay
        loop
        preload="metadata"
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          display: "block",
          //aspectRatio: "16 / 9", // ✅ scales with container width
        }}
      />
      <Box
        sx={{
          position: "absolute",
          left: 6,
          top: 6,
          px: 0.6,
          py: 0.25,
          borderRadius: 1,
          fontSize: 11,
          fontWeight: 900,
          bgcolor: "rgba(255,255,255)",
          border: "1px solid rgba(255,255,255,0.14)",
        }}
      >
        {seg.label ?? `#${seg.rallyIndex + 1}`}
      </Box>
    </Box>
  );
}
